import * as React from "react";
import { Button, ButtonToolbar, Form, Modal } from "react-bootstrap";

import { Loader } from "lib/components/Loader";
import { RolePopup } from "lib/components/RolePopup";
import I18n, { TranslationsObjectKeys } from "lib/I18n";
import style from "./style.module.css";

import { Role, Roles } from "../../constants";
import { observer } from "mobx-react-lite";
import { getStore } from "bundles/UserTitleRoleEditor/store";
import { useEffect } from "react";
import { BootstrapTable } from "lib/components/BootstrapTable";

const flexItemStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
};

export const RegisteredPane = observer(() => {
  const store = getStore().registered;
  useEffect(() => {
    store.listUserTitleRoles();
  }, []);

  const onRowSelect = (row: { loginid: string }, isSelected: boolean) => {
    store.selectUsers([row.loginid], isSelected);
    return true;
  };

  const onSelectAll = (rows: { loginid: string }[], isSelected: boolean) => {
    store.selectUsers(
      rows.map((row) => row.loginid),
      isSelected,
    );
    return true;
  };

  const onForceUpdateRoles = async () => {
    await store.updateUserTitleRole();
    store.alertShow = false;
  };

  const onUpdateRoles = () => {
    if (store.shouldShowAlert()) {
      store.alertShow = true;
    } else {
      store.updateUserTitleRole();
    }
  };

  const onDeleteRoles = () => {
    /* eslint-disable no-alert */
    if (confirm(I18n.t("user_title_role_editor.messages.confirm_deletion"))) {
      /* eslint-enable no-alert */
      store.deleteUserTitleRole();
    }
  };

  const onHideModal = () => {
    store.alertShow = false;
  };

  const renderTable = () => {
    const tempRoles: { [index: string]: string } = {};
    Roles.forEach((role) => {
      tempRoles[role] = I18n.t(`roles.${role}`);
    });

    const actualUserTitleRoles = store.userTitleRoles.map((_) => ({
      ..._,
      titleRole: I18n.t(`roles.${_.titleRole}` as TranslationsObjectKeys),
    }));

    return (
      <BootstrapTable
        data={actualUserTitleRoles}
        columns={[
          {
            dataField: "name",
            label: I18n.t("user_title_role_editor.label.name"),
            placeholder: `${I18n.t("user_title_role_editor.label.name")}...`,
          },
          {
            dataField: "loginid",
            label: I18n.t("user_title_role_editor.label.loginid"),
            placeholder: `${I18n.t("user_title_role_editor.label.loginid")}...`,
          },
          {
            dataField: "titleRole",
            label: I18n.t("user_title_role_editor.label.role"),
            placeholder: `${I18n.t("user_title_role_editor.label.role")}...`,
            options: Roles.map((role) => ({
              label: I18n.t(`roles.${role}`),
              value: role,
            })),
          },
        ]}
        idField="loginid"
        selectedIds={actualUserTitleRoles
          .filter((_) => _.isSelected)
          .map((_) => _.loginid)}
        onRowSelect={onRowSelect}
        onSelectAll={onSelectAll}
      />
    );
  };

  const renderModal = () => {
    return (
      <Modal
        animation={false}
        size="lg"
        show={store.alertShow}
        onHide={onHideModal}
      >
        <Modal.Header>
          <Modal.Title>
            {I18n.t("user_title_role_editor.label.confirm")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflow: "auto" }}>
          {renderModalBody()}
        </Modal.Body>
        <Modal.Footer>{renderModalFooter()}</Modal.Footer>
      </Modal>
    );
  };

  const renderModalBody = () => {
    const userNames = store.notNclUsers().map((_) => _.name || _.loginid);
    return (
      <div>
        <p>{I18n.t("user_title_role_editor.messages.role_warning1")}</p>
        <ul>
          {userNames.map((userName, i) => (
            <li key={i}>{userName}</li>
          ))}
        </ul>
        <p>{I18n.t("user_title_role_editor.messages.role_warning2")}</p>
      </div>
    );
  };

  const renderModalFooter = () => {
    return (
      <div>
        <Button variant="primary" onClick={onForceUpdateRoles}>
          {I18n.t("user_title_role_editor.action.update")}
        </Button>
        <Button variant="light" onClick={onHideModal}>
          {I18n.t("user_title_role_editor.action.cancel")}
        </Button>
      </div>
    );
  };

  const canModify =
    store.userTitleRoles.some((_) => _.isSelected) &&
    !store.isModifying &&
    !store.isLoading;

  return (
    <div className={style.root}>
      <Form.Group className={style.tableSection}>
        <Form.Label>
          {I18n.t("user_title_role_editor.label.registered_users")}
        </Form.Label>
        <p>{I18n.t("user_title_role_editor.messages.registered")}</p>
        <Loader
          show={store.isLoading}
          style={{ ...flexItemStyle, minHeight: 0, overflow: "hidden" }}
        >
          {renderTable()}
        </Loader>
      </Form.Group>
      <Form.Group>
        <Form.Label>{I18n.t("user_title_role_editor.label.role")}</Form.Label>
        <RolePopup placement="top" />
        <Form.Select
          onChange={(e) => (store.role = e.currentTarget.value as Role)}
        >
          {Roles.map((role, i) => (
            <option value={role} key={i}>
              {I18n.t(`roles.${role}`)}
            </option>
          ))}
        </Form.Select>
        <Form.Text muted>
          {I18n.t("user_title_role_editor.messages.role_registered")}
        </Form.Text>
      </Form.Group>
      <ButtonToolbar>
        <Button
          className="me-2"
          variant="light"
          onClick={onUpdateRoles}
          disabled={!canModify}
        >
          {I18n.t("user_title_role_editor.action.update")}
        </Button>
        <Button variant="danger" onClick={onDeleteRoles} disabled={!canModify}>
          {I18n.t("user_title_role_editor.action.delete")}
        </Button>
      </ButtonToolbar>
      <div>{renderModal()}</div>
    </div>
  );
});
